import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { api } from '../api/client';
import FreeSpinCountdown from '../components/FreeSpinCountdown';
import StrainAvailability from '../components/StrainAvailability';
import WelcomeModal from '../components/WelcomeModal';

const GREETINGS = [
  "Stay lifted, stay loyal.",
  "Another day, another dab.",
  "Roll up and rack up those points!",
  "Good vibes and fat clouds ahead.",
  "Your loyalty is higher than your tolerance.",
  "Puff, puff, earn!",
  "The daily grind just got greener.",
  "Blaze your way to the top.",
  "Keep calm and carry on toking.",
  "Loyalty hits different.",
];

function getRandomGreeting() {
  const lastIdx = parseInt(sessionStorage.getItem('lastGreetingIdx'), 10);
  let idx;
  do { idx = Math.floor(Math.random() * GREETINGS.length); }
  while (idx === lastIdx && GREETINGS.length > 1);
  sessionStorage.setItem('lastGreetingIdx', String(idx));
  return GREETINGS[idx];
}

const fmtTime = (t) => {
  const [h, m] = t.split(':').map(Number);
  const hr = h % 12 || 12;
  const ap = h < 12 ? 'am' : 'pm';
  return m === 0 ? `${hr}${ap}` : `${hr}:${String(m).padStart(2, '0')}${ap}`;
};

const TIER_COLORS = { light: '#a0aec0', medium: '#ecc94b', heavy: '#805ad5', ultra: '#ed8936' };

export default function DashboardPage() {
  const { user, refreshUser } = useAuth();
  const location = useLocation();

  const [showWelcome, setShowWelcome]         = useState(false);
  const [tickBalance, setTickBalance]         = useState(null);
  const [pendingOrders, setPendingOrders]     = useState(null);
  const [savings, setSavings]                 = useState(null);
  const [catalog, setCatalog]                 = useState([]);
  const [newOrderGlow, setNewOrderGlow]       = useState(false);
  const [cancelling, setCancelling]           = useState(null);
  const [applyingCoupon, setApplyingCoupon]   = useState(null);
  const [removingCoupon, setRemovingCoupon]   = useState(null);
  const [shopStatus, setShopStatus]           = useState(null);
  const [streak, setStreak]                   = useState(0);
  const [closestAch, setClosestAch]           = useState(null);
  const [securityOpen, setSecurityOpen]       = useState(false);
  const [activeSlide, setActiveSlide]         = useState(0);
  const swiperRef                             = useRef(null);

  // Post-order glow
  useEffect(() => {
    if (!location.state?.newOrder) return;
    setNewOrderGlow(true);
    window.history.replaceState({}, document.title);
    const t = setTimeout(() => setNewOrderGlow(false), 5000);
    return () => clearTimeout(t);
  }, [location.state]);

  const fetchUserData = useCallback(() => {
    if (!user) return;
    api.get('/bookings/my-ticks').then(d => {
      setTickBalance(parseInt(d.tick_count) > 0 ? d : null);
    }).catch(() => {});
    api.get('/bookings/my-pending').then(d => {
      setPendingOrders(d.pending_count > 0 ? d : null);
    }).catch(() => {});
    api.get('/bookings/my-savings').then(d => {
      if (d.coupons_used > 0) setSavings(d);
    }).catch(() => {});
    api.get('/rewards/catalog').then(setCatalog).catch(() => {});
    refreshUser();
  }, [user?.id, refreshUser]); // eslint-disable-line

  // Core data + welcome check + polling
  useEffect(() => {
    api.get('/spins/history').then(data => {
      if (user && data.length === 0 && user.spin_tokens > 0) {
        const key = `welcome_dismissed_${user.id}`;
        if (!localStorage.getItem(key)) setShowWelcome(true);
      }
    }).catch(() => {});
    fetchUserData();
    const iv = setInterval(fetchUserData, 5000);
    return () => clearInterval(iv);
  }, [fetchUserData, user]);

  // Opening hours → shop status badge
  useEffect(() => {
    api.get('/opening-hours').then(data => {
      const now = new Date();
      const todayH = data.find(h => Number(h.day_of_week) === now.getDay());
      let isOpen = false;
      if (todayH?.is_open && todayH.open_time && todayH.close_time) {
        const [oh, om] = todayH.open_time.split(':').map(Number);
        const [ch, cm] = todayH.close_time.split(':').map(Number);
        const nowM = now.getHours() * 60 + now.getMinutes();
        const openM = oh * 60 + om;
        const closeM = ch * 60 + cm;
        isOpen = closeM === 0 || closeM <= openM ? nowM >= openM : nowM >= openM && nowM < closeM;
      }
      const DAY_SHORT = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
      let nextLabel = null;
      for (let i = 0; i <= 7; i++) {
        const d = new Date(now); d.setDate(d.getDate() + i);
        const dh = data.find(h => Number(h.day_of_week) === d.getDay());
        if (!dh?.is_open || !dh.open_time) continue;
        if (i === 0) {
          const [oh, om] = dh.open_time.split(':').map(Number);
          if (now.getHours() * 60 + now.getMinutes() < oh * 60 + om) {
            nextLabel = `Opens today ${fmtTime(dh.open_time)}`; break;
          }
          if (isOpen && dh.close_time) { nextLabel = `Closes ${fmtTime(dh.close_time)}`; break; }
          continue;
        }
        nextLabel = `Opens ${i === 1 ? 'tomorrow' : DAY_SHORT[d.getDay()]} ${fmtTime(dh.open_time)}`; break;
      }
      setShopStatus({ isOpen, nextLabel });
    }).catch(() => {});
  }, []);

  // Achievements → streak + closest teaser
  useEffect(() => {
    if (!user) return;
    api.get('/achievements').then(data => {
      if (data?.streak) setStreak(data.streak.current_streak || 0);
      if (data?.achievements) {
        const incomplete = data.achievements.filter(
          a => !a.completed && a.progress > 0 && a.effective_requirement > 0
        );
        if (incomplete.length > 0) {
          setClosestAch(incomplete.reduce((best, a) =>
            a.progress / a.effective_requirement > best.progress / best.effective_requirement ? a : best
          ));
        }
      }
    }).catch(() => {});
  }, [user]);

  const dismissWelcome = () => {
    setShowWelcome(false);
    if (user) localStorage.setItem(`welcome_dismissed_${user.id}`, '1');
  };

  const handleCancelOrder = async (id) => {
    if (!window.confirm('Cancel this item?')) return;
    setCancelling(id);
    try {
      await api.put(`/bookings/${id}/cancel`);
      const d = await api.get('/bookings/my-pending');
      setPendingOrders(d.pending_count > 0 ? d : null);
    } catch { /* ignore */ } finally { setCancelling(null); }
  };

  const handleApplyCoupon = async (id) => {
    setApplyingCoupon(id);
    try {
      await api.put(`/bookings/${id}/apply-coupon`);
      const d = await api.get('/bookings/my-pending');
      setPendingOrders(d.pending_count > 0 ? d : null);
    } catch { /* ignore */ } finally { setApplyingCoupon(null); }
  };

  const handleRemoveCoupon = async (id) => {
    setRemovingCoupon(id);
    try {
      await api.put(`/bookings/${id}/remove-coupon`);
      const d = await api.get('/bookings/my-pending');
      setPendingOrders(d.pending_count > 0 ? d : null);
    } catch { /* ignore */ } finally { setRemovingCoupon(null); }
  };

  // Derived values
  const greeting   = useMemo(getRandomGreeting, []);
  const levelData  = user?.level_data;
  const xpPct      = levelData ? Math.round((levelData.progress || 0) * 100) : 0;
  const points     = user?.points || 0;

  const sortedTiers   = useMemo(() => [...catalog].sort((a, b) => a.cost - b.cost), [catalog]);
  const nextTier      = sortedTiers.find(r => r.cost > points);
  const prevTier      = [...sortedTiers].reverse().find(r => r.cost <= points);
  const atMax         = catalog.length > 0 && !nextTier;
  const progressBase  = prevTier?.cost ?? 0;
  const progressTarget = nextTier?.cost ?? sortedTiers[sortedTiers.length - 1]?.cost ?? 1;
  const pointsPct     = atMax ? 100 : Math.min(100, Math.round(((points - progressBase) / (progressTarget - progressBase)) * 100));

  // Achievement teaser colours
  const achTc  = closestAch ? (TIER_COLORS[closestAch.tier] || TIER_COLORS.light) : null;
  const achPct = closestAch ? Math.min(100, Math.round((closestAch.progress / closestAch.effective_requirement) * 100)) : 0;

  return (
    <div>
      {showWelcome && (
        <WelcomeModal username={user?.username} spinTokens={user?.spin_tokens || 0} onClose={dismissWelcome} />
      )}

      {/* ── Hero ───────────────────────────────────────────────────────────── */}
      <div style={{
        background: 'linear-gradient(135deg, rgba(56,161,105,0.12), rgba(56,161,105,0.03))',
        border: '1px solid rgba(56,161,105,0.2)',
        borderRadius: 'var(--radius)',
        padding: '20px 20px 16px',
        marginBottom: 16,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginBottom: 10 }}>
          {levelData && (
            <div style={{
              width: 52, height: 52, borderRadius: '50%', flexShrink: 0,
              background: 'linear-gradient(135deg, var(--accent), #2f855a)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: 20, fontWeight: 800, color: '#fff',
              boxShadow: '0 0 18px rgba(56,161,105,0.5)',
            }}>
              {levelData.level}
            </div>
          )}
          <div style={{ flex: 1, minWidth: 0 }}>
            <h1 style={{ margin: 0, fontSize: 22, lineHeight: 1.2 }}>
              What's good, {user?.username}!
            </h1>
            {levelData?.title && (
              <div style={{ fontSize: 12, color: 'var(--accent)', fontWeight: 600, marginTop: 3, letterSpacing: 0.3 }}>
                {levelData.title}
              </div>
            )}
          </div>
        </div>

        <p style={{ fontSize: 14, color: 'var(--success)', fontStyle: 'italic', margin: '0 0 14px' }}>
          🌿 {greeting}
        </p>

        {levelData && (
          <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, color: 'var(--text-muted)', marginBottom: 5 }}>
              <span>Level {levelData.level} XP</span>
              {levelData.is_max_level
                ? <span style={{ color: 'var(--warning)', fontWeight: 600 }}>⭐ Max Level</span>
                : <span>{xpPct}% to Level {levelData.level + 1}</span>}
            </div>
            <div style={{ height: 5, borderRadius: 3, background: 'rgba(255,255,255,0.08)', overflow: 'hidden' }}>
              <div style={{
                width: `${xpPct}%`, height: '100%', borderRadius: 3,
                background: levelData.is_max_level
                  ? 'linear-gradient(90deg, var(--warning), #f6e05e)'
                  : 'linear-gradient(90deg, var(--accent), #68d391)',
                transition: 'width 1s ease',
                boxShadow: '0 0 6px rgba(56,161,105,0.5)',
              }} />
            </div>
          </div>
        )}
      </div>

      {/* ── Alert cards swiper ───────────────────────────────────────────────── */}
      {(() => {
        const slides = [
          tickBalance && (
            <div key="tick" className="card" style={{
              margin: '0 0 0 0',
              background: 'linear-gradient(135deg, rgba(192,86,33,0.15), rgba(192,86,33,0.05))',
              border: '1px solid rgba(192,86,33,0.4)',
            }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8 }}>
                <div>
                  <h3 style={{ margin: 0, color: '#f6ad55', fontSize: 15 }}>Outstanding Balance</h3>
                  <p style={{ margin: '4px 0 0', fontSize: 13, color: 'var(--text-secondary)' }}>
                    You have {tickBalance.tick_count} unpaid order{parseInt(tickBalance.tick_count) !== 1 ? 's' : ''} on tick
                  </p>
                </div>
                <div style={{ textAlign: 'right' }}>
                  <div style={{ fontSize: 28, fontWeight: 700, color: '#f6ad55' }}>
                    {parseFloat(tickBalance.total_owed).toLocaleString('en-GB', { style: 'currency', currency: 'GBP' })}
                  </div>
                  <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>owed</div>
                </div>
              </div>
            </div>
          ),

          pendingOrders && (
            <div key="pending" className={`card${newOrderGlow ? ' new-order-glow' : ''}`} style={{
              margin: 0,
              background: 'linear-gradient(135deg, rgba(56,161,105,0.15), rgba(56,161,105,0.05))',
              border: '1px solid rgba(56,161,105,0.4)',
            }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8 }}>
                <div>
                  <h3 style={{ margin: 0, color: 'var(--success)', fontSize: 15 }}>Pending Order</h3>
                  <p style={{ margin: '4px 0 0', fontSize: 13, color: 'var(--text-secondary)' }}>
                    {pendingOrders.pending_count} item{pendingOrders.pending_count !== 1 ? 's' : ''} awaiting pickup
                  </p>
                </div>
                <div style={{ textAlign: 'right' }}>
                  {(() => {
                    const paidItems = pendingOrders.items.filter(i => !i.applied_coupon_id);
                    const paidTotal = paidItems.reduce((s, i) => s + (parseFloat(i.price) || 0), 0);
                    const hasCoupon = pendingOrders.items.some(i => i.applied_coupon_id);
                    return (
                      <>
                        <div style={{ fontSize: 28, fontWeight: 700, color: 'var(--text-primary)' }}>
                          {paidTotal > 0
                            ? paidTotal.toLocaleString('en-GB', { style: 'currency', currency: 'GBP' })
                            : `${pendingOrders.total_grams}g`}
                        </div>
                        <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>
                          {paidTotal > 0 ? 'to pay' : 'total'}{hasCoupon && ' (coupon applied)'}
                        </div>
                      </>
                    );
                  })()}
                </div>
              </div>
              <div style={{ marginTop: 10, display: 'flex', flexDirection: 'column', gap: 6 }}>
                {pendingOrders.items.map(item => (
                  <div key={item.id} style={{
                    background: item.applied_coupon_id ? 'rgba(214,188,250,0.15)' : 'rgba(56,161,105,0.15)',
                    border: `1px solid ${item.applied_coupon_id ? 'rgba(214,188,250,0.4)' : 'rgba(56,161,105,0.3)'}`,
                    borderRadius: 'var(--radius-sm)', padding: '6px 10px', fontSize: 12, color: 'var(--text-secondary)',
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                      <span>{item.amount} {item.strain_name || 'Any'}</span>
                      {item.applied_coupon_id
                        ? <span style={{ color: '#d6bcfa', fontWeight: 600 }}>FREE (coupon applied)</span>
                        : parseFloat(item.price) > 0 && <span style={{ color: 'var(--text-primary)', fontWeight: 600 }}>&pound;{parseFloat(item.price).toFixed(2)}</span>
                      }
                      <button
                        onClick={() => handleCancelOrder(item.id)}
                        disabled={cancelling === item.id}
                        style={{ background: 'none', border: 'none', color: 'var(--danger)', cursor: 'pointer', fontSize: 14, padding: '0 2px', lineHeight: 1, fontWeight: 700, marginLeft: 'auto' }}
                        title="Cancel this item"
                      >&times;</button>
                    </div>
                    {item.applied_coupon_id ? (
                      <div style={{ marginTop: 4, display: 'flex', alignItems: 'center', gap: 6, fontSize: 11 }}>
                        <span style={{ color: '#d6bcfa' }}>{item.applied_coupon_detail} &mdash; {item.applied_coupon_code}</span>
                        <button
                          onClick={() => handleRemoveCoupon(item.id)}
                          disabled={removingCoupon === item.id}
                          style={{ background: 'rgba(252,129,129,0.15)', border: '1px solid rgba(252,129,129,0.3)', borderRadius: 'var(--radius-sm)', padding: '2px 8px', fontSize: 10, color: 'var(--danger)', cursor: 'pointer' }}
                        >{removingCoupon === item.id ? '...' : 'Remove Coupon'}</button>
                      </div>
                    ) : (
                      item.matching_coupons > 0 && (
                        <div style={{ marginTop: 4 }}>
                          <button
                            onClick={() => handleApplyCoupon(item.id)}
                            disabled={applyingCoupon === item.id}
                            style={{ background: 'rgba(214,188,250,0.2)', border: '1px solid rgba(214,188,250,0.4)', borderRadius: 'var(--radius-sm)', padding: '3px 10px', fontSize: 11, color: '#d6bcfa', cursor: 'pointer', fontWeight: 600 }}
                          >{applyingCoupon === item.id ? 'Applying...' : `Apply Reward Coupon (${item.matching_coupons} available)`}</button>
                        </div>
                      )
                    )}
                  </div>
                ))}
              </div>
              <div style={{ marginTop: 8, fontSize: 12, color: 'var(--text-muted)', display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
                <span>
                  Pickup: {new Date(pendingOrders.items[0].pickup_time).toLocaleString('en-GB', {
                    day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit',
                  })}
                </span>
                {pendingOrders.batch_previews && Object.keys(pendingOrders.batch_previews).length > 0
                  ? Object.values(pendingOrders.batch_previews).map((bp, idx) => bp.points > 0 && (
                      <span key={idx} style={{ color: 'var(--warning)', fontWeight: 600 }}>
                        Batch rewards: {bp.tier_desc} tiers &rarr; {bp.points} pts, {bp.spins} spin{bp.spins !== 1 ? 's' : ''}
                      </span>
                    ))
                  : pendingOrders.total_points > 0 && (
                      <span style={{ color: 'var(--warning)', fontWeight: 600 }}>+{pendingOrders.total_points} pts on completion</span>
                    )
                }
                {pendingOrders.items.some(i => i.applied_coupon_id) && (
                  <span style={{ color: '#d6bcfa', fontWeight: 600 }}>Coupon items: no pts/spins</span>
                )}
              </div>
              <div style={{ marginTop: 10 }}>
                <a href="https://t.me/Club_Greenhouse" target="_blank" rel="noopener noreferrer" className="contact-seller-btn">
                  Contact Seller
                </a>
              </div>
            </div>
          ),

          savings && (
            <div key="savings" className="card" style={{
              margin: 0,
              background: 'linear-gradient(135deg, rgba(72,187,120,0.15), rgba(72,187,120,0.05))',
              border: '1px solid rgba(72,187,120,0.4)',
            }}>
              <h3 style={{ margin: '0 0 8px', color: 'var(--success)', fontSize: 15 }}>Your Savings</h3>
              <div className="savings-grid" style={{ gap: 8, textAlign: 'center' }}>
                <div>
                  <div style={{ fontSize: 22, fontWeight: 700, color: 'var(--success)' }}>
                    {savings.total_savings.toLocaleString('en-GB', { style: 'currency', currency: 'GBP' })}
                  </div>
                  <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>saved</div>
                </div>
                <div>
                  <div style={{ fontSize: 22, fontWeight: 700, color: 'var(--text-primary)' }}>{savings.coupons_used}</div>
                  <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>tokens used</div>
                </div>
                <div>
                  <div style={{ fontSize: 22, fontWeight: 700, color: 'var(--text-primary)' }}>{savings.grams_redeemed}g</div>
                  <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>redeemed</div>
                </div>
              </div>
            </div>
          ),
        ].filter(Boolean);

        if (slides.length === 0) return null;
        if (slides.length === 1) return <div style={{ marginBottom: 16 }}>{slides[0]}</div>;

        return (
          <div style={{ marginBottom: 16 }}>
            <div
              ref={swiperRef}
              className="dash-swiper"
              onScroll={e => {
                const el = e.currentTarget;
                const idx = Math.round(el.scrollLeft / el.offsetWidth);
                setActiveSlide(idx);
              }}
            >
              {slides.map((slide, idx) => (
                <div key={idx} className="dash-swiper-slide">
                  {slide}
                </div>
              ))}
            </div>
            {/* Dot indicators */}
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 6, marginTop: 10 }}>
              {slides.map((_, idx) => (
                <button
                  key={idx}
                  onClick={() => {
                    const el = swiperRef.current;
                    if (el) el.scrollTo({ left: idx * el.offsetWidth, behavior: 'smooth' });
                    setActiveSlide(idx);
                  }}
                  style={{
                    width: activeSlide === idx ? 18 : 7,
                    height: 7,
                    borderRadius: 4,
                    background: activeSlide === idx ? 'var(--accent)' : 'var(--border)',
                    border: 'none',
                    padding: 0,
                    cursor: 'pointer',
                    transition: 'width 0.25s ease, background 0.25s ease',
                  }}
                />
              ))}
            </div>
          </div>
        );
      })()}

      <FreeSpinCountdown onClaimed={refreshUser} />

      {/* ── Stat grid (2×2) ─────────────────────────────────────────────────── */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 10, marginBottom: 16 }}>
        {[
          { icon: '🌿', value: (user?.points || 0).toLocaleString(), label: 'Bud Points',     color: 'var(--warning)',  accent: 'rgba(236,201,75,0.1)',  border: 'rgba(236,201,75,0.22)' },
          { icon: '🎰', value: user?.spin_tokens || 0,               label: 'Spin Tokens',    color: '#d6bcfa',         accent: 'rgba(214,188,250,0.1)', border: 'rgba(214,188,250,0.22)' },
          { icon: '🪙', value: user?.avatar_tokens || 0,             label: 'Avatar Tokens',  color: '#ed8936',         accent: 'rgba(237,137,54,0.1)',  border: 'rgba(237,137,54,0.22)' },
          { icon: '🔥', value: streak,                               label: 'Day Streak',     color: '#f6ad55',         accent: 'rgba(246,173,85,0.1)',  border: 'rgba(246,173,85,0.22)' },
        ].map(({ icon, value, label, color, accent, border }) => (
          <div key={label} style={{
            background: accent,
            border: `1px solid ${border}`,
            borderRadius: 'var(--radius)',
            padding: '14px 16px',
            display: 'flex',
            alignItems: 'center',
            gap: 12,
          }}>
            <span style={{ fontSize: 24, lineHeight: 1 }}>{icon}</span>
            <div>
              <div style={{ fontSize: 22, fontWeight: 800, color, lineHeight: 1 }}>{value}</div>
              <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 3 }}>{label}</div>
            </div>
          </div>
        ))}
      </div>

      {/* ── Closest achievement teaser ──────────────────────────────────────── */}
      {closestAch && (
        <Link to="/profile" style={{ textDecoration: 'none', display: 'block', marginBottom: 16 }}>
          <div style={{
            background: `${achTc}11`,
            border: `1px solid ${achTc}44`,
            borderRadius: 'var(--radius)',
            padding: '12px 16px',
            display: 'flex',
            alignItems: 'center',
            gap: 12,
          }}>
            <span style={{ fontSize: 26, lineHeight: 1, flexShrink: 0, filter: `drop-shadow(0 0 4px ${achTc}88)` }}>
              {closestAch.icon || '⭐'}
            </span>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 6 }}>
                <div>
                  <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-primary)' }}>{closestAch.title}</span>
                  <span style={{ fontSize: 11, color: 'var(--text-muted)', marginLeft: 8 }}>
                    {closestAch.progress} / {closestAch.effective_requirement}
                  </span>
                </div>
                <span style={{ fontSize: 12, fontWeight: 700, color: achTc, flexShrink: 0, marginLeft: 8 }}>{achPct}%</span>
              </div>
              <div style={{ height: 5, borderRadius: 3, background: 'rgba(255,255,255,0.08)', overflow: 'hidden' }}>
                <div style={{
                  width: `${achPct}%`, height: '100%', borderRadius: 3,
                  background: `linear-gradient(90deg, ${achTc}99, ${achTc})`,
                  boxShadow: `0 0 6px ${achTc}66`,
                }} />
              </div>
            </div>
            <span style={{ fontSize: 16, color: 'var(--text-muted)', flexShrink: 0 }}>›</span>
          </div>
        </Link>
      )}

      {/* ── Points progress bar ──────────────────────────────────────────────── */}
      {catalog.length > 0 && (
        <div className="card" style={{ marginBottom: 16 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 6 }}>
            <span style={{ fontSize: 13, color: 'var(--text-secondary)', fontWeight: 600 }}>
              {atMax ? '🏆 Max Tier Achieved' : `🎯 Next: ${nextTier.name}`}
            </span>
            <span style={{ fontSize: 13, fontWeight: 700, color: 'var(--warning)' }}>
              {atMax ? `${points.toLocaleString()} pts` : `${points.toLocaleString()} / ${nextTier.cost.toLocaleString()} pts`}
            </span>
          </div>
          <div style={{ background: 'var(--bg-secondary)', borderRadius: 8, height: 14, overflow: 'hidden' }}>
            <div style={{
              width: `${pointsPct}%`, height: '100%', borderRadius: 8,
              background: atMax
                ? 'linear-gradient(90deg, var(--success), var(--warning))'
                : 'linear-gradient(90deg, var(--success), var(--accent))',
              transition: 'width 0.8s ease',
              boxShadow: atMax ? '0 0 10px rgba(236,201,75,0.4)' : '0 0 10px rgba(56,161,105,0.35)',
            }} />
          </div>
          <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 4, display: 'flex', justifyContent: 'space-between' }}>
            <span>{atMax ? 'You can redeem any reward!' : `${(nextTier.cost - points).toLocaleString()} pts to go`}</span>
            <span>{pointsPct}%</span>
          </div>
        </div>
      )}

      {/* ── Nav cards ────────────────────────────────────────────────────────── */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 16, marginBottom: 16 }}>

        {/* Spin */}
        <Link to="/spin" className="card" style={{ textAlign: 'center', textDecoration: 'none', color: 'inherit', position: 'relative' }}>
          {user?.spin_tokens > 0 && (
            <div style={{
              position: 'absolute', top: 10, right: 10,
              background: 'rgba(56,161,105,0.25)', border: '1px solid rgba(56,161,105,0.5)',
              borderRadius: 12, padding: '2px 9px',
              fontSize: 11, fontWeight: 700, color: 'var(--success)',
              animation: 'achDotPulse 2s ease-in-out infinite',
            }}>
              {user.spin_tokens} ready
            </div>
          )}
          <div style={{ fontSize: 40, marginBottom: 8 }}>&#127807;</div>
          <h3>Spin the Leaf</h3>
          <p style={{ color: 'var(--text-muted)', fontSize: 14, margin: 0 }}>
            {user?.spin_tokens > 0
              ? `${user.spin_tokens} token${user.spin_tokens !== 1 ? 's' : ''} ready to blaze`
              : 'No tokens — re-up soon!'}
          </p>
        </Link>

        {/* Stash Shop */}
        <Link to="/rewards" className="card" style={{ textAlign: 'center', textDecoration: 'none', color: 'inherit' }}>
          <div style={{ fontSize: 40, marginBottom: 8 }}>&#128648;</div>
          <h3>Stash Shop</h3>
          <p style={{ color: 'var(--text-muted)', fontSize: 14, margin: 0 }}>
            {atMax
              ? 'Unlock any reward!'
              : nextTier
                ? `${(nextTier.cost - points).toLocaleString()} pts to ${nextTier.name}`
                : 'Cash in your Bud Points'}
          </p>
        </Link>

        {/* Pre-Order */}
        <Link to="/bookings" className="card" style={{ textAlign: 'center', textDecoration: 'none', color: 'inherit', position: 'relative' }}>
          {shopStatus !== null && (
            <div style={{
              position: 'absolute', top: 10, right: 10,
              background: shopStatus.isOpen ? 'rgba(56,161,105,0.2)' : 'rgba(113,128,150,0.15)',
              border: `1px solid ${shopStatus.isOpen ? 'rgba(56,161,105,0.5)' : 'rgba(113,128,150,0.35)'}`,
              borderRadius: 12, padding: '2px 9px',
              fontSize: 10, fontWeight: 700,
              color: shopStatus.isOpen ? 'var(--success)' : 'var(--text-muted)',
            }}>
              {shopStatus.isOpen ? '● Open' : '● Closed'}
            </div>
          )}
          <div style={{ fontSize: 40, marginBottom: 8 }}>&#128230;</div>
          <h3>Pre-Order Pickup</h3>
          <p style={{ color: 'var(--text-muted)', fontSize: 14, margin: 0 }}>
            {shopStatus?.nextLabel || 'Reserve your next sesh'}
          </p>
        </Link>

        {/* Profile */}
        <Link to="/profile" className="card" style={{ textAlign: 'center', textDecoration: 'none', color: 'inherit' }}>
          <div style={{ fontSize: 40, marginBottom: 8 }}>&#129412;</div>
          <h3>Your Stash</h3>
          <p style={{ color: 'var(--text-muted)', fontSize: 14, margin: 0 }}>
            {levelData?.title || 'View your reward nugs'}
          </p>
        </Link>
      </div>

      <StrainAvailability />

      {/* ── Security note (collapsible) ──────────────────────────────────────── */}
      {user?.last_login_at && (
        <div style={{ marginTop: 20, textAlign: 'center' }}>
          <button
            onClick={() => setSecurityOpen(p => !p)}
            style={{
              background: 'none', border: 'none', cursor: 'pointer',
              fontSize: 11, color: 'var(--text-muted)', textDecoration: 'underline', padding: 0,
            }}
          >
            {securityOpen ? 'Hide security info' : 'Security info'}
          </button>
          {securityOpen && (
            <div style={{
              marginTop: 8, fontSize: 11, color: '#888',
              background: 'var(--bg-secondary)', border: '1px solid var(--border)',
              borderRadius: 'var(--radius-sm)', padding: '8px 14px',
              display: 'inline-block',
            }}>
              Last login: {new Date(user.last_login_at).toLocaleString('en-GB', {
                day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit',
              })}
              {user.last_login_ip && <span style={{ marginLeft: 8 }}>· IP: {user.last_login_ip}</span>}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
